/**
 * Alta de varios usuarios de una vez, y sus contrasenas temporales.
 *
 * ===========================================================================
 * POR QUE UN LOTE Y NO DIEZ FORMULARIOS
 *
 * Diez agentes entran en dias. De uno en uno son diez formularios y diez
 * contrasenas elegidas con prisa, que en la practica acaban siendo la misma
 * repetida diez veces --y ese es el peor resultado posible, porque el segundo
 * que la descubre entra con la cuenta del primero--.
 *
 * Aqui las contrasenas las genera el servidor: doce caracteres sin ceros ni
 * oes, en tres grupos, pensados para dictarse [services/api/src/contrasena.ts].
 *
 * ===========================================================================
 * LA PANTALLA DE DESPUES ES LA IMPORTANTE
 *
 * Las contrasenas se ven UNA VEZ. No es una decision de interfaz que se pueda
 * suavizar: lo que queda en la base es su hash, y no hay forma de volver a
 * leerlas. Si alguien cierra esta pantalla sin copiarlas, la unica salida es
 * resetear una por una.
 *
 * Por eso el aviso no es una nota al pie sino lo primero que se lee, el boton
 * de copiar copia LAS DE TODOS de golpe --copiar diez a mano es donde se pierde
 * una-- y el boton de cerrar dice que se van a perder, no "Aceptar".
 *
 * ===========================================================================
 * TODO O NADA
 *
 * La API crea el lote en una transaccion. Un lote a medias dejaria al ADMIN
 * comparando la lista contra la tabla para saber cuales faltan; asi, un correo
 * repetido se corrige en el formulario --que sigue puesto, con lo escrito-- y
 * se vuelve a mandar entero.
 *
 * ===========================================================================
 * LA TABLA NO SE REPINTA AL TECLEAR
 *
 * Mismo patron que `vista/contrasena.ts` y `vista/login.ts`. Un manejador por
 * celda que reconstruye la tabla en cada tecla hace dos cosas malas a la vez:
 * tira el foco --se escribe una letra y el cursor se va-- y, si en vez de
 * repintar se acumula sobre `o.filas`, escribe encima de lo que pusieron las
 * otras celdas, porque ese array es el que habia cuando se construyo la vista.
 *
 * Aqui los controles van sueltos y se LEEN cuando hace falta: al mandar, y
 * antes de anadir o quitar una fila --que si repinta, y ahi el foco esta en un
 * boton que desaparece de todas formas--.
 */

import type { RolUsuario, UsuarioCreado } from '../api';
import { el } from '../dom';
import type { Traductor } from '../i18n';

export interface FilaLote {
  readonly nombre: string;
  readonly email: string;
  readonly rol: RolUsuario;
  /** `null` y no `false`: es una eleccion que hay que hacer. La API tambien la
   *  exige explicita [CLAUDE.md, Visibilidad]. */
  readonly veCosto: boolean | null;
}

export const FILA_VACIA: FilaLote = {
  nombre: '',
  email: '',
  rol: 'AGENTE',
  veCosto: null,
};

/** Tres filas al abrir. Ni una --que parece un formulario suelto-- ni diez, que
 *  dejan la pantalla llena de huecos que hay que ignorar. */
export const LOTE_INICIAL: readonly FilaLote[] = [FILA_VACIA, FILA_VACIA, FILA_VACIA];

const ROLES: readonly RolUsuario[] = ['AGENTE', 'SUPERVISOR', 'OPERADOR', 'ADMIN'];

export interface OpcionesLote {
  readonly t: Traductor;
  readonly filas: readonly FilaLote[];
  readonly trabajando: boolean;
  /** Los indices de las filas con algun hueco. La API valida lo mismo; esto
   *  evita el viaje. */
  readonly faltan: readonly number[];
  /** Solo al anadir o quitar una fila, nunca al teclear. Recibe lo que hay
   *  escrito en ese momento. Ver la cabecera. */
  readonly onCambio: (filas: readonly FilaLote[]) => void;
  /** Recibe las filas LEIDAS DEL DOM al pulsar Crear. */
  readonly onCrear: (filas: readonly FilaLote[]) => void;
  readonly onCancelar: () => void;
}

function entrada(
  valor: string,
  tipo: string,
  extra: Record<string, string> = {},
): HTMLInputElement {
  return el('input', { clase: 'control', attrs: { type: tipo, value: valor, ...extra } });
}

export function pintarLoteUsuarios(o: OpcionesLote): HTMLElement {
  const { t } = o;

  /** Los controles de cada fila, para poder leerlos cuando haga falta. */
  const controles: {
    nombre: HTMLInputElement;
    email: HTMLInputElement;
    rol: HTMLSelectElement;
    veCosto: HTMLSelectElement;
  }[] = [];

  /** Lo que hay escrito AHORA, no lo que habia al construir la vista. */
  const leer = (): FilaLote[] =>
    controles.map((c) => ({
      nombre: c.nombre.value,
      email: c.email.value,
      rol: c.rol.value as RolUsuario,
      veCosto: c.veCosto.value === '' ? null : c.veCosto.value === 'si',
    }));

  const filas = o.filas.map((f, i) => {
    const nombre = entrada(f.nombre, 'text', { maxlength: '200' });
    const email = entrada(f.email, 'email', {
      autocapitalize: 'none',
      spellcheck: 'false',
    });

    const rol = el('select', { clase: 'control' });
    for (const r of ROLES) {
      rol.appendChild(
        el('option', { texto: t(`usuarios.rol.${r}`), attrs: { value: r } }),
      );
    }
    rol.value = f.rol;

    const veCosto = el('select', { clase: 'control' });
    veCosto.appendChild(
      el('option', { texto: t('usuarios.elige'), attrs: { value: '' } }),
    );
    veCosto.appendChild(
      el('option', { texto: t('usuarios.veCosto.si'), attrs: { value: 'si' } }),
    );
    veCosto.appendChild(
      el('option', { texto: t('usuarios.veCosto.no'), attrs: { value: 'no' } }),
    );
    veCosto.value = f.veCosto === null ? '' : f.veCosto ? 'si' : 'no';

    controles.push({ nombre, email, rol, veCosto });

    return el('tr', { clase: o.faltan.includes(i) ? 'fila-espera' : undefined }, [
      el('td', {}, [nombre]),
      el('td', {}, [email]),
      el('td', {}, [rol]),
      el('td', {}, [veCosto]),
      el('td', {}, [
        // Quitar una fila solo cuando queda mas de una: con una sola, quitarla
        // dejaria un formulario sin nada que rellenar.
        o.filas.length === 1
          ? null
          : el('button', {
              clase: 'boton boton-plano',
              texto: t('lote.quitarFila'),
              attrs: {
                type: 'button',
                'aria-label': t('lote.quitarFilaN', { n: String(i + 1) }),
              },
              onClick: () => {
                // Se lee ANTES de repintar: si no, quitar la fila 3 borraria lo
                // que se hubiera escrito en la 1 y la 2.
                o.onCambio(leer().filter((_, j) => j !== i));
              },
            }),
      ]),
    ]);
  });

  const form = el('form', { clase: 'lote-form' }, [
    el('table', { clase: 'tabla-resultados tabla-lote' }, [
      el('thead', {}, [
        el('tr', {}, [
          el('th', { texto: t('usuarios.campo.nombre') }),
          el('th', { texto: t('usuarios.campo.email') }),
          el('th', { texto: t('usuarios.campo.rol') }),
          el('th', { texto: t('usuarios.col.veCosto') }),
          el('th', {}),
        ]),
      ]),
      el('tbody', {}, filas),
    ]),

    el('div', { clase: 'rfq-acciones' }, [
      el('button', {
        clase: 'boton boton-plano',
        texto: t('lote.anadirFila'),
        attrs: { type: 'button' },
        onClick: () => {
          o.onCambio([...leer(), FILA_VACIA]);
        },
      }),
      el('button', {
        clase: 'boton boton-primario',
        texto: o.trabajando
          ? t('lote.creando')
          : t('lote.crear', { n: String(o.filas.length) }),
        attrs: o.trabajando
          ? { type: 'submit', disabled: 'disabled' }
          : { type: 'submit' },
      }),
      el('button', {
        clase: 'boton boton-plano',
        texto: t('usuarios.cancelar'),
        attrs: { type: 'button' },
        onClick: o.onCancelar,
      }),
    ]),
  ]);

  form.addEventListener('submit', (ev) => {
    ev.preventDefault();
    if (!o.trabajando) o.onCrear(leer());
  });

  return el('section', { clase: 'panel usuarios-panel-alta' }, [
    el('h3', { texto: t('lote.titulo') }),
    el('p', { clase: 'tenue', texto: t('lote.explicacion') }),
    o.faltan.length === 0
      ? null
      : el('p', { clase: 'aviso aviso-error', texto: t('lote.faltan') }),
    form,
  ]);
}

// ---------------------------------------------------------------------------
// Las credenciales, una sola vez
// ---------------------------------------------------------------------------

export interface OpcionesCredenciales {
  readonly t: Traductor;
  readonly creados: readonly UsuarioCreado[];
  readonly copiado: boolean;
  readonly copiaManual: boolean;
  readonly onCopiar: (texto: string) => void;
  readonly onCerrar: () => void;
}

/** El texto que se copia: una linea por persona, lista para pegar en un chat. */
export function textoDeCredenciales(creados: readonly UsuarioCreado[]): string {
  return creados
    .map(
      (c) =>
        `${c.usuario.nombre} <${c.usuario.email}>: ${c.contrasena_temporal ?? '(la elegiste tu)'}`,
    )
    .join('\n');
}

export function pintarCredenciales(o: OpcionesCredenciales): HTMLElement {
  const { t } = o;
  const texto = textoDeCredenciales(o.creados);

  // El textarea existe para el respaldo de la copia: cuando
  // `navigator.clipboard` no esta --http sin TLS, un iframe sin permiso-- se
  // selecciona y se dice que hay que pulsar Ctrl+C. Mismo criterio que el
  // correo del RFQ.
  const respaldo = el('textarea', {
    clase: 'control credenciales-texto',
    texto,
    attrs: { readonly: 'readonly', rows: String(Math.min(o.creados.length + 1, 12)) },
  });

  return el('section', { clase: 'panel panel-credenciales' }, [
    // Lo primero que se lee, y en rojo: si esta pantalla se cierra sin copiar,
    // la unica salida es resetear una por una.
    el('div', { clase: 'aviso aviso-error' }, [
      el('strong', { texto: t('lote.unaVez.titulo') }),
      el('p', { texto: t('lote.unaVez.explicacion') }),
    ]),

    el('h3', { texto: t('lote.credenciales.titulo', { n: String(o.creados.length) }) }),

    el('table', { clase: 'tabla-resultados tabla-credenciales' }, [
      el('thead', {}, [
        el('tr', {}, [
          el('th', { texto: t('usuarios.campo.nombre') }),
          el('th', { texto: t('usuarios.campo.email') }),
          el('th', { texto: t('lote.credenciales.contrasena') }),
        ]),
      ]),
      el(
        'tbody',
        {},
        o.creados.map((c) =>
          el('tr', {}, [
            el('td', { texto: c.usuario.nombre }),
            el('td', {}, [el('code', { texto: c.usuario.email })]),
            el('td', {}, [
              c.contrasena_temporal === null
                ? el('span', { clase: 'tenue', texto: t('lote.credenciales.laElegiste') })
                : el('code', {
                    clase: 'contrasena-temporal',
                    texto: c.contrasena_temporal,
                  }),
            ]),
          ]),
        ),
      ),
    ]),

    respaldo,

    o.copiaManual
      ? el('p', { clase: 'aviso aviso-neutro', texto: t('lote.copiaManual') })
      : o.copiado
        ? el('p', { clase: 'aviso aviso-ok', texto: t('lote.copiado') })
        : null,

    el('div', { clase: 'rfq-acciones' }, [
      el('button', {
        clase: 'boton boton-primario',
        texto: t('lote.copiarTodas'),
        attrs: { type: 'button' },
        onClick: () => {
          o.onCopiar(texto);
        },
      }),
      // No dice "Aceptar": dice lo que pasa.
      el('button', {
        clase: 'boton boton-plano',
        texto: t('lote.cerrarYPerder'),
        attrs: { type: 'button' },
        onClick: o.onCerrar,
      }),
    ]),

    el('p', { clase: 'tenue', texto: t('lote.queHacenEllos') }),
  ]);
}
